// De tekening van een onderdeel uit het 3D-model (O-120). Puur: geen DOM, geen IO, geen LLM. De bouw-PDF
// (modules/uitvoer, uit de GLB van de snapshot of het project) en de kaart in de viewer (modules/3d/browser,
// uit de driehoeken die de viewer al heeft) geven hier de driehoeken van één onderdeel in mm. Dezelfde
// driehoeken geven altijd dezelfde uitkomst.
//
// Werkwijze:
// 1. Het assenstelsel van het onderdeel komt uit core/aanzichten.ts (naarOnderdeel): x langs de lengte,
//    y langs de breedte, z langs de dikte, zoals de zaaglijst. Welke kop links ligt en welk vlak boven,
//    volgt de gaten en de omtrek uit het ontwerp (`hint`), zodat "begin" en "eind" dezelfde zijn als in
//    de zaaglijst. De vorm zelf komt niet uit het ontwerp. Bij een concentrische ring zonder boorgaten
//    kan een facetrand andere begrenzingsmaten geven. Dan zoeken we een draaiing van dezelfde mesh
//    die bij de zaaglijst past, zonder de punten te schalen of de maattolerantie te verruimen.
// 2. Per zijde van het blok de vlakke driehoeken op die zijde. Hun randen die maar bij één driehoek
//    horen, vormen lussen: de buitenste lus is de omtrek van dat vlak.
// 3. De omtrek van boven: de omtrek van het bovenvlak en het ondervlak samen. Zijn ze verschillend (een
//    schuin zaagblad), dan de grootste, en de rand van de kleinste als lijn in de tekening.
// 4. Een ronde binnenlus is doorgaans een gat. Een ronde doorgaande binnenlus in het bovenaanzicht
//    die een gesloten ontwerppad volgt, of groter is dan GROOTSTE_BOOR_MM, is een uitsparing (O-332).
//    Een blind gat houdt zijn diepte: de afstand tot de bodem.
// 5. Het zijaanzicht (lengte × dikte) komt uit de lange zijvlakken, alleen als het geen rechthoek is.
//
// Controle (vergelijkMetZaaglijst): lengte, breedte, dikte, het aantal gaten en elke Ø moeten gelijk zijn
// aan de zaaglijst van de motor. Wijkt iets af, dan gebruikt de aanroeper de tekening uit het ontwerp.

import { naarOnderdeel, type Geometrie, type GatPunt, type V3 } from "./aanzichten.ts";
import type { SchetsGat, SchetsPad, SchetsVlak } from "./maatschets.ts";

export type { Geometrie } from "./aanzichten.ts";

type P2 = [number, number];

/** Zoveel mm telt als op een zijde van het blok, of als hetzelfde punt. */
const OP_ZIJDE_MM = 0.05;
/** Zoveel mm mag een gat aan de overkant verschoven liggen en nog hetzelfde gat zijn. */
const GAT_MM = 0.3;
/** Een hoekpunt zo dicht bij de rand van het blok ligt op die rand. */
const SNAP_MM = 0.25;
/** Maten die zoveel mm verschillen, tellen als gelijk bij de controle. */
const MAAT_MM = 0.5;
/** Een lus is rond als elk punt binnen 2 procent op dezelfde afstand van het midden ligt. */
const ROND = 0.02;
/**
 * Er is geen boorcatalogus. Voor O-332 geldt 100 mm als grootste gatzaag in een hobbywerkplaats.
 * Een grotere ronde doorgaande binnenlus is een uitsparing.
 */
export const GROOTSTE_BOOR_MM = 100;
/** Het model bevat de zaagbreedte; een ontwerppad mag daarom zoveel mm van de modelrand liggen. */
const PAD_MM = 1.6;

export interface ModelDoel {
  lengte_mm: number;
  breedte_mm: number;
  dikte_mm: number;
}

/** Het ontwerp kiest de kant en helpt ronde zaagsneden van boorgaten te onderscheiden. */
export interface ModelHint {
  gaten?: SchetsGat[];
  omtrek?: P2[];
  /** Alleen de paden van dit onderdeel, in hetzelfde lokale assenstelsel en in mm. */
  paden?: SchetsPad[];
}

/** Een lijn in het bovenaanzicht die geen omtrek is: de rand van een schuine kant. */
export interface ModelLijn {
  van: P2;
  tot: P2;
  /** Zie je hem van boven (bovenvlak) of ligt hij eronder (ondervlak, gestippeld)? */
  zichtbaar: boolean;
}

export interface ModelOnderdeel {
  lengte_mm: number;
  breedte_mm: number;
  dikte_mm: number;
  /** De omtrek van boven: [x, y] in mm, met de klok mee (y naar beneden) vanaf linksboven. */
  omtrek: P2[];
  /** Randen van een schuine kant in het bovenaanzicht. */
  lijnen: ModelLijn[];
  /** Het zijaanzicht [x, v] (v vanaf het bovenvlak omlaag), alleen als het geen rechthoek is. */
  zij?: P2[];
  gaten: SchetsGat[];
  /** Uitsparingen die door het onderdeel gaan, als gesloten pad in het bovenvlak. */
  paden: SchetsPad[];
}

// ------------------------------------------------------------------ vlakken en lussen

/** De zes zijden: as (0 x, 1 y, 2 z) en kant (0 bij het minimum, 1 bij het maximum). */
type Zijde = { as: 0 | 1 | 2; kant: 0 | 1 };

/** De twee assen in het vlak van een zijde, in de volgorde van de tekening. */
const VLAK_ASSEN: Record<0 | 1 | 2, [0 | 1 | 2, 0 | 1 | 2]> = { 2: [0, 1], 1: [0, 2], 0: [1, 2] };

const VLAK_NAAM: Record<string, SchetsVlak> = { "2,1": "boven", "2,0": "onder", "1,0": "voor", "1,1": "achter", "0,0": "kop_links", "0,1": "kop_rechts" };

interface Lus {
  punten: P2[];
  opp: number;
}

interface VlakLussen {
  /** De buitenste lussen (meer dan één: het vlak is in stukken gedeeld, bijvoorbeeld door een sleuf). */
  buiten: Lus[];
  binnen: Lus[];
}

function oppervlak(p: P2[]): number {
  let s = 0;
  for (let i = 0; i < p.length; i++) {
    const a = p[i]!;
    const b = p[(i + 1) % p.length]!;
    s += a[0] * b[1] - b[0] * a[1];
  }
  return s / 2;
}

function binnenVeelhoek(q: P2, p: P2[]): boolean {
  let binnen = false;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const [xi, yi] = p[i]!;
    const [xj, yj] = p[j]!;
    if (yi > q[1] !== yj > q[1] && q[0] < ((xj - xi) * (q[1] - yi)) / (yj - yi) + xi) binnen = !binnen;
  }
  return binnen;
}

function afstandTotRand(q: P2, p: P2[]): number {
  let min = Infinity;
  for (let i = 0; i < p.length; i++) {
    const a = p[i]!;
    const b = p[(i + 1) % p.length]!;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.max(0, Math.min(1, ((q[0] - a[0]) * dx + (q[1] - a[1]) * dy) / l2)) : 0;
    min = Math.min(min, Math.hypot(q[0] - a[0] - t * dx, q[1] - a[1] - t * dy));
  }
  return min;
}

/**
 * Classificatie van een reeds als rond en doorgaand herkende binnenlus in het bovenaanzicht.
 * Eerst het gesloten ontwerppad van hetzelfde onderdeel, daarna de grens voor de grootste boor.
 * Vergelijk beide volledige randen, inclusief de middens van de lijnstukken. Alleen dezelfde
 * eindpunten hebben is onvoldoende: een vierkant binnen een cirkel is niet die cirkel.
 */
export function isRondeDoorgaandeUitsparing(punten: P2[], diameter_mm: number, paden: readonly SchetsPad[] = []): boolean {
  const volgt = (a: P2[], b: P2[]) => a.every((p, i) => {
    const q = a[(i + 1) % a.length]!;
    return afstandTotRand(p, b) <= PAD_MM &&
      afstandTotRand([(p[0] + q[0]) / 2, (p[1] + q[1]) / 2], b) <= PAD_MM;
  });
  const volgtPad = paden.some((p) =>
    p.gesloten && p.punten.length >= 3 &&
    volgt(punten, p.punten) && volgt(p.punten, punten),
  );
  return volgtPad || diameter_mm > GROOTSTE_BOOR_MM;
}

/** De lussen van één zijde: randen die bij maar één driehoek van die zijde horen, aan elkaar geregen. */
function lussenVan(l: { punten: V3[]; tris: number[]; normalen: V3[] }, maten: V3, z: Zijde, niveau?: number): VlakLussen {
  const [u, v] = VLAK_ASSEN[z.as];
  const doel = niveau ?? (z.kant ? maten[z.as] : 0);
  const teken = z.kant ? 1 : -1;
  const telling = new Map<string, { a: number; b: number; n: number }>();
  for (let t = 0; t < l.tris.length; t += 3) {
    if (l.normalen[t / 3]![z.as] * teken < 0.999) continue;
    const ids = [l.tris[t]!, l.tris[t + 1]!, l.tris[t + 2]!];
    if (!ids.every((i) => Math.abs(l.punten[i]![z.as] - doel) <= OP_ZIJDE_MM)) continue;
    for (let k = 0; k < 3; k++) {
      const a = ids[k]!;
      const b = ids[(k + 1) % 3]!;
      const s = a < b ? `${a},${b}` : `${b},${a}`;
      const r = telling.get(s);
      if (r) r.n++;
      else telling.set(s, { a, b, n: 1 });
    }
  }
  const buren = new Map<number, number[]>();
  for (const r of telling.values()) {
    if (r.n !== 1) continue;
    buren.set(r.a, [...(buren.get(r.a) ?? []), r.b]);
    buren.set(r.b, [...(buren.get(r.b) ?? []), r.a]);
  }
  const gehad = new Set<string>();
  const lussen: Lus[] = [];
  for (const [start, nb] of buren) {
    for (const eerste of nb) {
      const s0 = start < eerste ? `${start},${eerste}` : `${eerste},${start}`;
      if (gehad.has(s0)) continue;
      gehad.add(s0);
      const ids = [start];
      let vorige = start;
      let hier = eerste;
      let veilig = 0;
      while (hier !== start && veilig++ < 100000) {
        ids.push(hier);
        const volgende = (buren.get(hier) ?? []).find((x) => x !== vorige && !gehad.has(x < hier ? `${x},${hier}` : `${hier},${x}`));
        if (volgende === undefined) break;
        gehad.add(volgende < hier ? `${volgende},${hier}` : `${hier},${volgende}`);
        vorige = hier;
        hier = volgende;
      }
      if (hier !== start || ids.length < 3) continue;
      const punten = ids.map((i): P2 => [l.punten[i]![u], l.punten[i]![v]]);
      lussen.push({ punten, opp: Math.abs(oppervlak(punten)) });
    }
  }
  lussen.sort((a, b) => b.opp - a.opp);
  const buiten: Lus[] = [];
  const binnen: Lus[] = [];
  for (const lus of lussen) {
    const erin = buiten.some((b) => binnenVeelhoek(midden(lus.punten), b.punten) && lus.punten.every((q) => binnenVeelhoek(q, b.punten) || afstandTotRand(q, b.punten) <= OP_ZIJDE_MM));
    (erin ? binnen : buiten).push(lus);
  }
  return { buiten, binnen };
}

function midden(p: P2[]): P2 {
  let x = 0;
  let y = 0;
  for (const q of p) {
    x += q[0];
    y += q[1];
  }
  return [x / p.length, y / p.length];
}

/** Een ronde lus: het midden en de Ø, of null. */
function cirkel(l: Lus): { c: P2; d: number } | null {
  if (l.punten.length < 8) return null;
  const c = midden(l.punten);
  const r = l.punten.map((q) => Math.hypot(q[0] - c[0], q[1] - c[1]));
  const gem = r.reduce((a, b) => a + b, 0) / r.length;
  if (gem < 0.25 || (Math.max(...r) - Math.min(...r)) / gem > ROND) return null;
  return { c, d: Math.round(gem * 2 * 100) / 100 };
}

// ------------------------------------------------------------------ omtrek

/**
 * Punten die minder dan VEREENVOUDIG_MM van de lijn tussen hun buren liggen, vallen weg: punten op één lijn,
 * dubbele punten, en de kleine kartels die de zaagsneden van een boog in de mesh laten (O-110).
 */
const VEREENVOUDIG_MM = 0.15;

function vereenvoudig(p: P2[]): P2[] {
  const uit = [...p];
  const afwijking = (i: number) => {
    const a = uit[(i + uit.length - 1) % uit.length]!;
    const b = uit[i]!;
    const c = uit[(i + 1) % uit.length]!;
    const dx = c[0] - a[0];
    const dy = c[1] - a[1];
    const l = Math.hypot(dx, dy);
    return l < 1e-9 ? Math.hypot(b[0] - a[0], b[1] - a[1]) : Math.abs((b[0] - a[0]) * dy - (b[1] - a[1]) * dx) / l;
  };
  while (uit.length > 3) {
    let min = Infinity;
    let wie = -1;
    for (let i = 0; i < uit.length; i++) {
      const w = afwijking(i);
      if (w < min) {
        min = w;
        wie = i;
      }
    }
    if (min >= VEREENVOUDIG_MM) break;
    uit.splice(wie, 1);
  }
  return uit;
}

/**
 * Een rand korter dan KERF_MM tussen twee langere randen is een restje van de zaagsnede in de mesh (de
 * kerf bij een verstek). Die valt weg: de twee buren lopen door tot ze elkaar snijden, als dat snijpunt
 * dichtbij ligt, en anders komt er één punt in het midden.
 */
const KERF_MM = 1.6;

function zonderKerf(p: P2[]): P2[] {
  const uit = [...p];
  const lengte = (i: number) => {
    const a = uit[i]!;
    const b = uit[(i + 1) % uit.length]!;
    return Math.hypot(b[0] - a[0], b[1] - a[1]);
  };
  for (let veilig = 0; veilig < 200 && uit.length > 3; veilig++) {
    let kort = -1;
    for (let i = 0; i < uit.length; i++) {
      const n = uit.length;
      if (lengte(i) < KERF_MM && lengte((i + n - 1) % n) > lengte(i) && lengte((i + 1) % n) > lengte(i) && (kort < 0 || lengte(i) < lengte(kort))) kort = i;
    }
    if (kort < 0) break;
    const n = uit.length;
    const a0 = uit[(kort + n - 1) % n]!;
    const a1 = uit[kort]!;
    const b0 = uit[(kort + 1) % n]!;
    const b1 = uit[(kort + 2) % n]!;
    // Snijpunt van de lijn a0-a1 met de lijn b0-b1.
    const d1: P2 = [a1[0] - a0[0], a1[1] - a0[1]];
    const d2: P2 = [b1[0] - b0[0], b1[1] - b0[1]];
    const noemer = d1[0] * d2[1] - d1[1] * d2[0];
    let punt: P2 = [(a1[0] + b0[0]) / 2, (a1[1] + b0[1]) / 2];
    if (Math.abs(noemer) > 1e-9) {
      const t = ((b0[0] - a0[0]) * d2[1] - (b0[1] - a0[1]) * d2[0]) / noemer;
      const s: P2 = [a0[0] + t * d1[0], a0[1] + t * d1[1]];
      if (Math.hypot(s[0] - a1[0], s[1] - a1[1]) <= 2 * KERF_MM && Math.hypot(s[0] - b0[0], s[1] - b0[1]) <= 2 * KERF_MM) punt = s;
    }
    uit.splice(kort, 2, punt);
    if (kort + 1 >= n) uit.splice(0, 1);
  }
  return uit;
}

/** Met de klok mee (y naar beneden) en vanaf het punt linksboven, op 0,001 mm. */
function netjes(p: P2[]): P2[] {
  const r = (w: number) => Math.round(w * 1000) / 1000 + 0;
  let q = vereenvoudig(zonderKerf(vereenvoudig(p))).map(([x, y]): P2 => [r(x), r(y)]);
  if (oppervlak(q) < 0) q = q.reverse();
  const start = q.reduce((best, a, i) => (a[1] < q[best]![1] - 1e-6 || (Math.abs(a[1] - q[best]![1]) <= 1e-6 && a[0] < q[best]![0]) ? i : best), 0);
  return [...q.slice(start), ...q.slice(0, start)];
}

/** Ligt elk punt van a op de rand van b? */
const opRand = (a: P2[], b: P2[]) => a.every((q) => afstandTotRand(q, b) <= OP_ZIJDE_MM);
/** Ligt elk punt van a in of op b? */
const binnenIn = (a: P2[], b: P2[]) => a.every((q) => binnenVeelhoek(q, b) || afstandTotRand(q, b) <= OP_ZIJDE_MM);

/** De randen van a die niet op de rand van b liggen. */
function eigenRanden(a: P2[], b: P2[]): Array<[P2, P2]> {
  const uit: Array<[P2, P2]> = [];
  for (let i = 0; i < a.length; i++) {
    const p = a[i]!;
    const q = a[(i + 1) % a.length]!;
    const m: P2 = [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
    if (afstandTotRand(m, b) > OP_ZIJDE_MM) uit.push([p, q]);
  }
  return uit;
}

function isConvex(p: P2[]): boolean {
  let teken = 0;
  for (let i = 0; i < p.length; i++) {
    const a = p[i]!;
    const b = p[(i + 1) % p.length]!;
    const c = p[(i + 2) % p.length]!;
    const k = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
    if (Math.abs(k) < 1e-6) continue;
    if (teken === 0) teken = Math.sign(k);
    else if (Math.sign(k) !== teken) return false;
  }
  return true;
}

function convexeRomp(pts: P2[]): P2[] {
  const op = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const kruis = (o: P2, a: P2, b: P2) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const onder: P2[] = [];
  for (const p of op) {
    while (onder.length >= 2 && kruis(onder[onder.length - 2]!, onder[onder.length - 1]!, p) <= 1e-9) onder.pop();
    onder.push(p);
  }
  const boven: P2[] = [];
  for (const p of op.reverse()) {
    while (boven.length >= 2 && kruis(boven[boven.length - 2]!, boven[boven.length - 1]!, p) <= 1e-9) boven.pop();
    boven.push(p);
  }
  return [...onder.slice(0, -1), ...boven.slice(0, -1)];
}

/**
 * De omtrek van twee tegenover elkaar liggende vlakken samen, gezien vanaf het eerste (`a`). Gelijk: die
 * omtrek. Ligt de ene binnen de andere (een schuin zaagblad of een sponning), dan de grootste, en de
 * randen van de kleinste als lijn. Anders, als beide convex zijn, de convexe romp. Zonder bruikbaar vlak: null.
 */
function samen(a: Lus | null, b: Lus | null): { omtrek: P2[]; lijnen: ModelLijn[] } | null {
  if (!a && !b) return null;
  if (!a || !b) return { omtrek: (a ?? b)!.punten, lijnen: [] };
  const [pa, pb] = [a.punten, b.punten];
  if (opRand(pa, pb) && opRand(pb, pa)) return { omtrek: pa, lijnen: [] };
  const lijn = (zichtbaar: boolean) => ([van, tot]: [P2, P2]): ModelLijn => ({ van, tot, zichtbaar });
  if (binnenIn(pa, pb)) return { omtrek: pb, lijnen: eigenRanden(pa, pb).map(lijn(true)) };
  if (binnenIn(pb, pa)) return { omtrek: pa, lijnen: eigenRanden(pb, pa).map(lijn(false)) };
  if (isConvex(pa) && isConvex(pb)) {
    const romp = convexeRomp([...pa, ...pb]);
    return { omtrek: romp, lijnen: [...eigenRanden(pa, romp).map(lijn(true)), ...eigenRanden(pb, romp).map(lijn(false))] };
  }
  return null;
}

// ------------------------------------------------------------------ unie van vlakken op verschillende niveaus

/** Coordinaten die minder dan zoveel mm verschillen, zijn dezelfde coordinaat; een rand is recht als hij zo weinig afwijkt. */
const EXACT_MM = 0.005;
/** Boven deze aantallen geven we het op (null, dus de ontwerptekening): rekentijd, geen benadering. */
const MAX_NIVEAUS = 64;
const MAX_WERK = 20_000_000;

/**
 * De exacte silhouet van het model langs `as`: de unie van alle vlakken met een normaal langs die as, op elk
 * niveau (de bovenkant van de stroken naast een sleuf, de bodem van de sleuf, en de onderkanten). Gaat alleen
 * over rechthoekige (rechtlijnige) vlakken, op de coordinaten zelf, zonder raster van vaste stap en zonder
 * romp. Elk vlak waarvan we niet bewijzen dat het in de unie past, geeft null:
 *  - een schuine driehoek (normaal niet langs de as en niet loodrecht erop);
 *  - een lus die niet rechtlijnig is (behalve ronde binnenlussen: dat zijn gaten die elders getekend worden);
 *  - lussen die niet de volledige oppervlakte van de driehoeken van hun niveau beslaan;
 *  - een unie in meer dan een stuk, of met stukken die elkaar alleen in een hoekpunt raken.
 * Anders is het resultaat de buitenste rand van wat het model echt heeft, met de gaten die er echt in zitten
 * (rechtlijnige gaten door alle niveaus, bijvoorbeeld een venster door twee tegenover elkaar liggende kruisverbindingen).
 */
/** Dezelfde hoekpunten (op 0,002 mm), in beide richtingen: geen hoekpunt erbij of weg. */
function zelfdeHoeken(a: P2[], b: P2[]): boolean {
  const heeft = (rij: P2[], q: P2) => rij.some((r) => Math.abs(r[0] - q[0]) <= 0.002 && Math.abs(r[1] - q[1]) <= 0.002);
  return a.length === b.length && a.every((q) => heeft(b, q)) && b.every((q) => heeft(a, q));
}

function silhouet(l: { punten: V3[]; tris: number[]; normalen: V3[] }, maten: V3, as: 0 | 1 | 2): { omtrek: P2[]; gaten: P2[][] } | null {
  const [u, v] = VLAK_ASSEN[as];
  // Per richting de driehoeken op hun niveau.
  const per: Record<1 | -1, Array<{ w: number; opp: number }>> = { 1: [], [-1]: [] };
  for (let t = 0; t < l.tris.length; t += 3) {
    const na = l.normalen[t / 3]![as];
    if (Math.abs(na) < 0.001) continue;
    if (Math.abs(na) < 0.999) return null;
    const ps = [l.punten[l.tris[t]!]!, l.punten[l.tris[t + 1]!]!, l.punten[l.tris[t + 2]!]!];
    const ws = ps.map((p) => p[as]);
    if (Math.max(...ws) - Math.min(...ws) > OP_ZIJDE_MM) return null;
    const opp = Math.abs((ps[1]![u] - ps[0]![u]) * (ps[2]![v] - ps[0]![v]) - (ps[2]![u] - ps[0]![u]) * (ps[1]![v] - ps[0]![v])) / 2;
    per[na > 0 ? 1 : -1].push({ w: ws[0]!, opp });
  }
  const groepen: VlakLussen[] = [];
  for (const teken of [1, -1] as const) {
    const lijst = per[teken].sort((a, b) => a.w - b.w);
    for (let i = 0; i < lijst.length; ) {
      const start = lijst[i]!.w;
      let opp = 0;
      while (i < lijst.length && lijst[i]!.w - start <= OP_ZIJDE_MM / 2) opp += lijst[i++]!.opp;
      if (groepen.length >= MAX_NIVEAUS) return null;
      const g = lussenVan(l, maten, { as, kant: teken > 0 ? 1 : 0 }, start);
      const gedekt = g.buiten.reduce((s, x) => s + x.opp, 0) - g.binnen.reduce((s, x) => s + x.opp, 0);
      if (Math.abs(gedekt - opp) > 0.01 + 1e-6 * opp) return null;
      // Een ronde binnenlus op een tussenliggend niveau (een boorgat door een sleufbodem) wordt niet als gat gelezen.
      const buitenNiveau = Math.abs(start) <= OP_ZIJDE_MM || Math.abs(start - maten[as]) <= OP_ZIJDE_MM;
      if (!buitenNiveau && g.binnen.some((x) => cirkel(x))) return null;
      groepen.push(g);
    }
  }
  if (!groepen.length) return null;

  // Alle lussen die meedoen: buitenlussen, en rechtlijnige binnenlussen (een ronde binnenlus is een gat).
  const doen: Array<{ lus: Lus; buiten: boolean; groep: number }> = [];
  groepen.forEach((g, groep) => {
    for (const lus of g.buiten) doen.push({ lus, buiten: true, groep });
    for (const lus of g.binnen) if (!cirkel(lus)) doen.push({ lus, buiten: false, groep });
  });
  const cluster = (waarden: number[]) => {
    const op = [...waarden].sort((a, b) => a - b);
    const uit: number[] = [];
    for (const w of op) if (!uit.length || w - uit[uit.length - 1]! > EXACT_MM) uit.push(w);
    return uit;
  };
  const xs = cluster(doen.flatMap((d) => d.lus.punten.map((p) => p[0])));
  const ys = cluster(doen.flatMap((d) => d.lus.punten.map((p) => p[1])));
  const index = (rij: number[], w: number) => {
    let lo = 0;
    for (let k = 0; k < rij.length; k++) if (rij[k]! <= w + EXACT_MM) lo = k;
    return lo;
  };
  const lussen = doen.map((d) => {
    const pts = d.lus.punten.map((p): [number, number] => [index(xs, p[0]), index(ys, p[1])]);
    return { ...d, pts };
  });
  for (const d of lussen) {
    for (let i = 0; i < d.pts.length; i++) {
      const a = d.pts[i]!;
      const b = d.pts[(i + 1) % d.pts.length]!;
      if (a[0] !== b[0] && a[1] !== b[1]) return null;
    }
  }
  const nx = xs.length - 1;
  const ny = ys.length - 1;
  if (nx < 1 || ny < 1 || nx * ny * Math.max(1, lussen.reduce((s, d) => s + d.pts.length, 0)) > MAX_WERK) return null;
  const geknipt = lussen.map((d) => ({ ...d, p: d.pts.map(([i, j]): P2 => [xs[i]!, ys[j]!]) }));
  const dek = new Uint8Array(nx * ny);
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      const q: P2 = [(xs[i]! + xs[i + 1]!) / 2, (ys[j]! + ys[j + 1]!) / 2];
      // Een cel is vlak als een groep hem heeft: in een buitenlus en niet in een rechtlijnige binnenlus van die groep.
      const erin = new Set<number>();
      const eruit = new Set<number>();
      for (const d of geknipt) if (binnenVeelhoek(q, d.p)) (d.buiten ? erin : eruit).add(d.groep);
      dek[i * ny + j] = [...erin].some((g) => !eruit.has(g)) ? 1 : 0;
    }
  }
  const isDek = (i: number, j: number) => i >= 0 && j >= 0 && i < nx && j < ny && dek[i * ny + j] === 1;
  // Randen tussen vlak en niet-vlak, met het vlak steeds aan dezelfde kant.
  const volg = new Map<string, [number, number]>();
  const key = (i: number, j: number) => `${i},${j}`;
  let aantal = 0;
  const rand = (a: [number, number], b: [number, number]) => {
    const k = key(a[0], a[1]);
    if (volg.has(k)) return false;
    volg.set(k, b);
    aantal++;
    return true;
  };
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      if (!isDek(i, j)) continue;
      if (!isDek(i, j - 1) && !rand([i, j], [i + 1, j])) return null;
      if (!isDek(i + 1, j) && !rand([i + 1, j], [i + 1, j + 1])) return null;
      if (!isDek(i, j + 1) && !rand([i + 1, j + 1], [i, j + 1])) return null;
      if (!isDek(i - 1, j) && !rand([i, j + 1], [i, j])) return null;
    }
  }
  if (!aantal) return null;
  // Alle randen aan lussen rijgen: een buitenrand en eventueel gaten (het vlak heeft daar echt niets).
  const ringen: Array<Array<[number, number]>> = [];
  const gehad = new Set<string>();
  for (const [begin] of volg) {
    if (gehad.has(begin)) continue;
    const ring: Array<[number, number]> = [];
    let k = begin;
    while (!gehad.has(k)) {
      gehad.add(k);
      const hier = k.split(",").map(Number) as [number, number];
      ring.push(hier);
      const volgende = volg.get(k);
      if (!volgende) return null;
      k = key(volgende[0], volgende[1]);
    }
    if (k !== begin) return null;
    ringen.push(ring);
  }
  const naarMm = (ring: Array<[number, number]>): P2[] => {
    const hoeken = ring.filter((p, k) => {
      const a = ring[(k + ring.length - 1) % ring.length]!;
      const b = ring[(k + 1) % ring.length]!;
      return (p[0] - a[0]) * (b[1] - p[1]) - (p[1] - a[1]) * (b[0] - p[0]) !== 0;
    });
    return hoeken.map(([i, j]): P2 => [xs[i]!, ys[j]!]);
  };
  const lijst = ringen.map(naarMm).sort((a, b) => Math.abs(oppervlak(b)) - Math.abs(oppervlak(a)));
  const [uit, ...gaten] = lijst;
  if (!uit || uit.length < 4) return null;
  // Een gat heeft de andere draairichting dan de buitenrand; twee buitenranden zijn twee losse stukken.
  if (gaten.some((g) => g.length < 4 || Math.sign(oppervlak(g)) === Math.sign(oppervlak(uit)))) return null;
  // netjes() (kerf en vereenvoudigen) mag er niets aan veranderen: dezelfde hoekpunten, anders null.
  for (const r of [uit, ...gaten]) if (!zelfdeHoeken(netjes(r), r)) return null;
  return { omtrek: uit, gaten };
}

// ------------------------------------------------------------------ gaten

interface RuwGat {
  zijde: Zijde;
  c: P2;
  d: number;
  /** Alleen bij een volledige ronde binnenlus, niet bij boorgaten die in een andere uitsparing opgaan. */
  lus?: Lus;
}

/** De diepte van een blind gat: de afstand van de zijde tot de bodem (een vlak naar dezelfde kant, binnen de cirkel). */
function diepteVan(l: { punten: V3[]; tris: number[]; normalen: V3[] }, maten: V3, g: RuwGat): number | null {
  const [u, v] = VLAK_ASSEN[g.zijde.as];
  const teken = g.zijde.kant ? 1 : -1;
  const vlak = g.zijde.kant ? maten[g.zijde.as] : 0;
  let beste: number | null = null;
  for (let t = 0; t < l.tris.length; t += 3) {
    if (l.normalen[t / 3]![g.zijde.as] * teken < 0.999) continue;
    const ps = [l.punten[l.tris[t]!]!, l.punten[l.tris[t + 1]!]!, l.punten[l.tris[t + 2]!]!];
    const w = ps[0]![g.zijde.as];
    if (Math.abs(w - vlak) <= OP_ZIJDE_MM) continue;
    if (!ps.every((p) => Math.hypot(p[u] - g.c[0], p[v] - g.c[1]) <= g.d / 2 + OP_ZIJDE_MM)) continue;
    const diep = Math.abs(vlak - w);
    if (beste === null || diep < beste) beste = diep;
  }
  return beste === null ? null : Math.round(beste * 100) / 100;
}

function gatUit(g: RuwGat, maten: V3, door: boolean, diepte: number | null): SchetsGat {
  const vlak = VLAK_NAAM[`${g.zijde.as},${g.zijde.kant}`]!;
  const [a, b] = g.c;
  const r2 = (w: number) => Math.round(w * 100) / 100 + 0;
  if (g.zijde.as === 2) return { x_mm: r2(a), y_mm: r2(b), diameter_mm: g.d, door, diepte_mm: diepte, vlak };
  if (g.zijde.as === 1) return { x_mm: r2(a), y_mm: g.zijde.kant ? r2(maten[1]) : 0, z_mm: r2(b), diameter_mm: g.d, door, diepte_mm: diepte, vlak };
  return { x_mm: g.zijde.kant ? r2(maten[0]) : 0, y_mm: r2(a), z_mm: r2(b), diameter_mm: g.d, door, diepte_mm: diepte, vlak };
}

/**
 * O-332: een concentrische ring heeft geen vaste richting in het plaatvlak. De facetten van de ronde
 * buitenrand kunnen naarOnderdeel een richting laten kiezen waarin de begrenzingsmaten kleiner zijn
 * dan in de zaaglijst. Zoek uitsluitend voor zo'n ring een passende richting van dezelfde punten.
 *
 * Beide vlakken moeten dezelfde ronde buitenrand en dezelfde concentrische, doorgaande uitsparing
 * hebben. Boorgaten leggen wel een richting vast en sluiten deze correctie uit. Een gevonden richting
 * moet aan de gewone maatcontrole voldoen. Er wordt niets geschaald, bijgetekend of uitgerekt.
 */
function richtRondeRing<T extends { punten: V3[]; tris: number[]; normalen: V3[]; maten: V3 }>(l: T, doel: ModelDoel, hint: ModelHint): T {
  const afwijking = (lengte: number, breedte: number) =>
    Math.max(Math.abs(lengte - doel.lengte_mm), Math.abs(breedte - doel.breedte_mm));
  const huidigeAfwijking = afwijking(l.maten[0], l.maten[1]);
  if (huidigeAfwijking <= MAAT_MM || hint.gaten?.length || Math.abs(l.maten[2] - doel.dikte_mm) > MAAT_MM) return l;

  const boven = lussenVan(l, l.maten, { as: 2, kant: 1 });
  const onder = lussenVan(l, l.maten, { as: 2, kant: 0 });
  if (boven.buiten.length !== 1 || onder.buiten.length !== 1 || boven.binnen.length !== 1 || onder.binnen.length !== 1) return l;
  const buitenBoven = boven.buiten[0]!;
  const buitenOnder = onder.buiten[0]!;
  const binnenBoven = boven.binnen[0]!;
  const binnenOnder = onder.binnen[0]!;
  if (!opRand(buitenBoven.punten, buitenOnder.punten) || !opRand(buitenOnder.punten, buitenBoven.punten) ||
      !opRand(binnenBoven.punten, binnenOnder.punten) || !opRand(binnenOnder.punten, binnenBoven.punten)) return l;

  const buiten = cirkel(buitenBoven);
  const binnen = cirkel(binnenBoven);
  if (!buiten || !binnen || binnen.d >= buiten.d ||
      Math.hypot(buiten.c[0] - binnen.c[0], buiten.c[1] - binnen.c[1]) > GAT_MM) return l;
  if (!isRondeDoorgaandeUitsparing(binnenBoven.punten, binnen.d, hint.paden)) return l;
  if (diepteVan(l, l.maten, { zijde: { as: 2, kant: 1 }, ...binnen }) !== null ||
      diepteVan(l, l.maten, { zijde: { as: 2, kant: 0 }, ...binnen }) !== null) return l;

  let beste: { cos: number; sin: number; minX: number; minY: number; lengte: number; breedte: number; fout: number } | null = null;
  // De begrenzing herhaalt zich na een halve draai. Een kwart graad per stap is voldoende om een
  // passende richting binnen de bestaande halve millimeter te vinden; geen uitkomst is ook toegestaan.
  for (let stap = 1; stap < 720; stap++) {
    const hoek = stap * Math.PI / 720;
    const cos = Math.cos(hoek);
    const sin = Math.sin(hoek);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of l.punten) {
      const x = cos * p[0] - sin * p[1];
      const y = sin * p[0] + cos * p[1];
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    const lengte = maxX - minX;
    const breedte = maxY - minY;
    const fout = afwijking(lengte, breedte);
    if (fout < (beste?.fout ?? huidigeAfwijking)) beste = { cos, sin, minX, minY, lengte, breedte, fout };
  }
  if (!beste || beste.fout > MAAT_MM) return l;
  const { cos, sin, minX, minY, lengte, breedte } = beste;
  return {
    ...l,
    punten: l.punten.map(([x, y, z]): V3 => [cos * x - sin * y - minX, sin * x + cos * y - minY, z]),
    normalen: l.normalen.map(([x, y, z]): V3 => [cos * x - sin * y, sin * x + cos * y, z]),
    maten: [lengte, breedte, l.maten[2]] as V3,
  };
}

// ------------------------------------------------------------------ ingang

/**
 * Voor de kant: hoeveel gaten uit het ontwerp in het model op hun eigen zijde liggen (boven of onder, voor
 * of achter), en daarna hoeveel hoekpunten van de omtrek en gesloten paden op een punt van het model liggen.
 */
function kantScore(hint: ModelHint): ((punten: V3[]) => number) | undefined {
  const gaten = hint.gaten ?? [];
  const omtrek = [...(hint.omtrek ?? []), ...(hint.paden ?? []).filter((p) => p.gesloten).flatMap((p) => p.punten)];
  if (!gaten.length && !omtrek.length) return undefined;
  return (punten) => {
    const max: V3 = [0, 1, 2].map((k) => Math.max(...punten.map((p) => p[k]!))) as V3;
    let n = 0;
    for (const g of gaten) {
      const as = g.vlak === "boven" || g.vlak === "onder" ? 2 : g.vlak === "voor" || g.vlak === "achter" ? 1 : 0;
      const vlak = g.vlak === "boven" || g.vlak === "achter" || g.vlak === "kop_rechts" ? max[as] : 0;
      const [u, v] = VLAK_ASSEN[as];
      const hart = [g.x_mm, g.y_mm, g.z_mm ?? 0];
      let op = 0;
      for (const p of punten) {
        if (Math.abs(p[as] - vlak) > OP_ZIJDE_MM) continue;
        if (Math.abs(Math.hypot(p[u] - hart[u]!, p[v] - hart[v]!) - g.diameter_mm / 2) < 0.25) op++;
        if (op >= 4) break;
      }
      if (op >= 4) n += 1000;
    }
    for (const h of omtrek) if (punten.some((p) => Math.abs(p[0] - h[0]) <= MAAT_MM && Math.abs(p[1] - h[1]) <= MAAT_MM)) n++;
    return n;
  };
}

/**
 * Gaten uit het ontwerp die in een uitsparing opgaan (twee greepgaten met een zaagsnede ertussen): de rand
 * van de uitsparing volgt een deel van de cirkel. Zo'n gat telt als gat, met zijn plek uit het model.
 */
function gatenInUitsparing(lus: Lus, hint: SchetsGat[], zijde: Zijde): RuwGat[] {
  const uit: RuwGat[] = [];
  for (const g of hint) {
    const as = g.vlak === "boven" || g.vlak === "onder" ? 2 : g.vlak === "voor" || g.vlak === "achter" ? 1 : 0;
    if (as !== zijde.as) continue;
    const [u, v] = VLAK_ASSEN[as];
    const hart = [g.x_mm, g.y_mm, g.z_mm ?? 0];
    const c: P2 = [hart[u]!, hart[v]!];
    const op = lus.punten.filter((q) => Math.abs(Math.hypot(q[0] - c[0], q[1] - c[1]) - g.diameter_mm / 2) < 0.1);
    if (op.length >= 4) uit.push({ zijde, c, d: g.diameter_mm });
  }
  return uit;
}

/**
 * Het onderdeel uit zijn driehoeken (mm, willekeurige assen), of null als de geometrie niet bij `doel`
 * past of de omtrek niet te vinden is. `hint` kiest de kant en classificeert ronde uitsparingen;
 * de maten en de getekende lussen komen uit het model.
 */
export function onderdeelUitModel(geo: Geometrie, doel: ModelDoel, hint: ModelHint = {}): ModelOnderdeel | null {
  const gatPunten: GatPunt[] = (hint.gaten ?? []).map((g) => ({
    x_mm: g.x_mm, y_mm: g.y_mm, z_mm: g.z_mm ?? 0, diameter_mm: g.diameter_mm,
    as: g.vlak === "boven" || g.vlak === "onder" ? 2 : g.vlak === "voor" || g.vlak === "achter" ? 1 : 0,
  }));
  let l;
  try {
    l = naarOnderdeel(geo, [doel.lengte_mm, doel.breedte_mm, doel.dikte_mm], gatPunten, kantScore(hint));
  } catch {
    return null;
  }
  if (!l) return null;
  l = richtRondeRing(l, doel, hint);
  const maten = l.maten;
  const zijden: Zijde[] = [{ as: 2, kant: 1 }, { as: 2, kant: 0 }, { as: 1, kant: 0 }, { as: 1, kant: 1 }, { as: 0, kant: 0 }, { as: 0, kant: 1 }];
  const vlakken = new Map<string, VlakLussen>(zijden.map((z) => [`${z.as},${z.kant}`, lussenVan(l, maten, z)]));
  const enkel = (z: string): Lus | null => {
    const v = vlakken.get(z)!;
    return v.buiten.length === 1 ? v.buiten[0]! : null;
  };

  // Omtrek van boven uit boven- en ondervlak.
  // Een vlak dat in stukken is gedeeld (een sleuf over de volle lengte): de exacte unie van alle vlakken, of null.
  let unieGaten: P2[][] = [];
  let geweigerd = false;
  let zijGat = false;
  const vlakOmtrek = (as: 1 | 2): { omtrek: P2[]; lijnen: ModelLijn[]; unie?: true } | null => {
    if (vlakken.get(`${as},1`)!.buiten.length <= 1 && vlakken.get(`${as},0`)!.buiten.length <= 1) return samen(enkel(`${as},1`), enkel(`${as},0`));
    const s = silhouet(l, maten, as);
    // Het zijaanzicht kent geen gaten: dan geen model. Een geweigerde unie is geen ontbrekend zijaanzicht; zie de controle na vlakOmtrek(1).
    if (!s || (as === 1 && s.gaten.length)) {
      geweigerd = true;
      if (s) zijGat = true;
      return null;
    }
    if (as === 2) unieGaten = s.gaten;
    return { omtrek: s.omtrek, lijnen: [], unie: true };
  };
  const boven = vlakOmtrek(2);
  if (!boven) return null;
  // Een hoekpunt vlak bij de rand van het blok ligt op die rand (de zaagsnede van een boog eindigt 0,1 mm ernaast).
  const snap = (w: number, max: number) => (Math.abs(w) <= SNAP_MM ? 0 : Math.abs(w - max) <= SNAP_MM ? max : w);
  const opRandVanBlok = (p: P2[], u: number, v: number): P2[] => p.map(([x, y]): P2 => [snap(x, u), snap(y, v)]);
  const omtrek = netjes(opRandVanBlok(boven.omtrek, maten[0], maten[1]));
  // De omtrek moet het hele blok beslaan; een stukje vlak (een taps onderdeel) is geen omtrek.
  const vult = (p: P2[], u: number, v: number) => {
    const xs = p.map((q) => q[0]);
    const ys = p.map((q) => q[1]);
    return Math.min(...xs) <= MAAT_MM && Math.abs(Math.max(...xs) - u) <= MAAT_MM && Math.min(...ys) <= MAAT_MM && Math.abs(Math.max(...ys) - v) <= MAAT_MM;
  };
  if (omtrek.length < 3 || !vult(omtrek, maten[0], maten[1])) return null;
  // De unie is exact: snap en netjes() mogen er geen hoekpunt aan veranderen (een inkeping vullen of een uitstulping weghalen).
  if (boven.unie && !zelfdeHoeken(omtrek, boven.omtrek)) return null;

  // Zijaanzicht uit voor- en achtervlak, in (x, v) met v vanaf het bovenvlak omlaag.
  const zijVlak = vlakOmtrek(1);
  // Een weggelaten zijaanzicht blijft zoals voorheen toegestaan bij een gewone omtrek (een schuin onderdeel). Is de omtrek
  // alleen via de unie gevonden (nieuw toegelaten vorm), of zat er een echt gat in het zijaanzicht, dan zou informatie
  // verdwijnen: dan null.
  if (geweigerd && (boven.unie || zijGat)) return null;
  let zij: P2[] | undefined;
  if (zijVlak) {
    const z = netjes(opRandVanBlok(zijVlak.omtrek.map(([x, zz]): P2 => [x, maten[2] - zz]), maten[0], maten[2]));
    const rechthoek = z.length === 4 && z.every(([x, y]) => (Math.abs(x) <= OP_ZIJDE_MM || Math.abs(x - maten[0]) <= OP_ZIJDE_MM) && (Math.abs(y) <= OP_ZIJDE_MM || Math.abs(y - maten[2]) <= OP_ZIJDE_MM));
    if (zijVlak.unie && !zelfdeHoeken(z, zijVlak.omtrek.map(([x, zz]): P2 => [x, maten[2] - zz]))) return null;
    if (!rechthoek && z.length >= 3 && vult(z, maten[0], maten[2])) zij = z;
    else if (!rechthoek && zijVlak.unie) return null;
  }

  // Gaten en uitsparingen: de binnenlussen per zijde, per as met de overkant vergeleken.
  const gaten: SchetsGat[] = [];
  const paden: SchetsPad[] = [];
  for (const as of [2, 1, 0] as const) {
    const ruw: Record<0 | 1, RuwGat[]> = { 0: [], 1: [] };
    const anders: Record<0 | 1, Lus[]> = { 0: [], 1: [] };
    for (const kant of [0, 1] as const) {
      for (const lus of vlakken.get(`${as},${kant}`)!.binnen) {
        const c = cirkel(lus);
        if (c) ruw[kant].push({ zijde: { as, kant }, ...c, lus });
        else {
          anders[kant].push(lus);
          ruw[kant].push(...gatenInUitsparing(lus, hint.gaten ?? [], { as, kant }));
        }
      }
    }
    const zelfde = (a: RuwGat, b: RuwGat) => Math.hypot(a.c[0] - b.c[0], a.c[1] - b.c[1]) <= GAT_MM && Math.abs(a.d - b.d) <= 0.2;
    const gebruikt = new Set<RuwGat>();
    // Door: dezelfde cirkel aan beide kanten. Het gat staat bij boven, achter of kop links.
    const eerst: 0 | 1 = as === 0 ? 0 : 1;
    const tweede: 0 | 1 = eerst ? 0 : 1;
    for (const g of ruw[eerst]) {
      const tegen = ruw[tweede].find((h) => !gebruikt.has(h) && zelfde(g, h));
      // Twee blinde gaten tegenover elkaar hebben een bodem: die gaan niet door.
      if (tegen && diepteVan(l, maten, g) === null && diepteVan(l, maten, tegen) === null) {
        gebruikt.add(g).add(tegen);
        if (as === 2 && g.lus && tegen.lus && isRondeDoorgaandeUitsparing(g.lus.punten, g.d, hint.paden)) {
          // Bewaar de binnenrand uit het model, precies één keer, zonder hem als boorgat te tellen.
          paden.push({ punten: netjes(g.lus.punten), gesloten: true, onder: false });
        } else {
          gaten.push(gatUit(g, maten, true, null));
        }
      }
    }
    for (const kant of [eerst, tweede]) {
      for (const g of ruw[kant]) {
        if (gebruikt.has(g)) continue;
        gaten.push(gatUit(g, maten, false, diepteVan(l, maten, g)));
      }
    }
    // Een uitsparing die door het bovenvlak en het ondervlak gaat, als pad in het bovenaanzicht.
    if (as === 2) {
      for (const lus of anders[1]) {
        const tegen = anders[0].some((b) => opRand(lus.punten, b.punten) && opRand(b.punten, lus.punten));
        if (tegen) paden.push({ punten: netjes(lus.punten), gesloten: true, onder: false });
      }
    }
  }
  // Gaten in de unie van de vlakken: het model heeft daar echt niets. Als pad, tenzij het al een pad is.
  for (const g of unieGaten) {
    const pad = netjes(g);
    if (!paden.some((p) => opRand(p.punten, pad) && opRand(pad, p.punten))) paden.push({ punten: pad, gesloten: true, onder: false });
  }
  gaten.sort((a, b) => a.vlak.localeCompare(b.vlak) || a.x_mm - b.x_mm || a.y_mm - b.y_mm || (a.z_mm ?? 0) - (b.z_mm ?? 0));
  const r = (w: number) => Math.round(w * 100) / 100;
  return {
    lengte_mm: r(maten[0]),
    breedte_mm: r(maten[1]),
    dikte_mm: r(maten[2]),
    omtrek,
    lijnen: boven.lijnen.map((x) => ({ ...x, van: [r(x.van[0]), r(x.van[1])], tot: [r(x.tot[0]), r(x.tot[1])] })),
    ...(zij ? { zij } : {}),
    gaten,
    paden,
  };
}

/** Wat de zaaglijst van de motor over een stuk zegt: de maten en de gaten uit de bewerking. */
export interface ZaaglijstStuk extends ModelDoel {
  /** Per soort gat het aantal en de Ø, zoals gatenUitBewerking in core/onderdeeltekst.ts. */
  gaten: Array<{ aantal: number; diameter_mm: number }>;
  /** De stralen uit de bewerking (stralenUitBewerking), voor de tekst bij een boog. */
  stralen?: number[];
}

/**
 * De verschillen tussen het model en de zaaglijst, als zinnen voor het log. Leeg: alles gelijk.
 * Lengte, breedte en dikte binnen 0,5 mm, evenveel gaten, en elke Ø uit het model in de zaaglijst.
 */
export function vergelijkMetZaaglijst(m: ModelOnderdeel, z: ZaaglijstStuk): string[] {
  const uit: string[] = [];
  for (const [naam, a, b] of [["lengte", m.lengte_mm, z.lengte_mm], ["breedte", m.breedte_mm, z.breedte_mm], ["dikte", m.dikte_mm, z.dikte_mm]] as const) {
    if (Math.abs(a - b) > MAAT_MM) uit.push(`${naam} ${a} in het model, ${b} in de zaaglijst`);
  }
  const verwacht = z.gaten.reduce((n, g) => n + g.aantal, 0);
  if (m.gaten.length !== verwacht) uit.push(`${m.gaten.length} gaten in het model, ${verwacht} in de zaaglijst`);
  const over = z.gaten.flatMap((g) => Array.from({ length: g.aantal }, () => g.diameter_mm));
  for (const g of m.gaten) {
    const i = over.findIndex((d) => Math.abs(d - g.diameter_mm) <= 0.2);
    if (i < 0) uit.push(`gat Ø ${g.diameter_mm} in het model staat niet in de zaaglijst`);
    else over.splice(i, 1);
  }
  return uit;
}
